import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../../context/AuthContext';
import { DeleteConfirmDialog } from '../../components/common/DeleteConfirmDialog';
import { GtSampleCard } from '../../components/common/GtSampleCard';
import { LoadMoreBar } from '../../components/common/LoadMoreBar';
import { usePagedGtSamples } from '../../hooks/usePagedGtSamples';
import { EvalApiService } from '../../services/EvalApiService';
import { EvalGtDataset, EvalGtSample } from '../../types';

export const GtSamplesListPage: React.FC = () => {
  const { token } = useAuthContext();
  const navigate = useNavigate();

  const [datasets, setDatasets] = useState<EvalGtDataset[]>([]);

  // Default to the unassigned pool — the most common reason to open this page
  const [filterDatasetId, setFilterDatasetId] = useState('__unassigned__');
  const [filterChainId, setFilterChainId] = useState('');
  const [filterIngestedBy, setFilterIngestedBy] = useState('');

  useEffect(() => {
    EvalApiService.listDatasets(token ?? '').then(({ items }) => setDatasets(items));
  }, [token]);

  const [deleteTarget, setDeleteTarget] = useState<EvalGtSample | null>(null);

  const { samples, total, loading, loadMore, reload } = usePagedGtSamples(token ?? '', {
    dataset_id:
      filterDatasetId && filterDatasetId !== '__unassigned__' ? filterDatasetId : undefined,
    unassigned: filterDatasetId === '__unassigned__' ? true : undefined,
    chain_id: filterChainId ? Number(filterChainId) : undefined,
    ingested_by: filterIngestedBy || undefined,
  });

  return (
    <div className="page-content">
      <div className="eval-page-header">
        <div>
          <div className="eval-page-title">GT Samples</div>
          <div className="eval-page-sub">{total} samples</div>
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          gap: 8,
          marginBottom: 20,
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        <select
          className="eval-filter-select"
          value={filterDatasetId}
          onChange={(e) => setFilterDatasetId(e.target.value)}
        >
          <option value="">All datasets</option>
          <option value="__unassigned__">— unassigned —</option>
          {datasets.map((d) => (
            <option key={d.dataset_id} value={d.dataset_id}>
              {d.dataset_name}
            </option>
          ))}
        </select>
        <input
          className="eval-form-input"
          placeholder="chain_id"
          value={filterChainId}
          onChange={(e) => setFilterChainId(e.target.value.replace(/\D/g, ''))}
          style={{ width: 110, fontFamily: 'var(--font-mono)', fontSize: 12 }}
        />
        <input
          className="eval-form-input"
          placeholder="ingested by"
          value={filterIngestedBy}
          onChange={(e) => setFilterIngestedBy(e.target.value)}
          style={{ width: 180, fontSize: 12 }}
        />
        {(filterDatasetId || filterChainId || filterIngestedBy) && (
          <button
            className="eval-btn-secondary"
            onClick={() => {
              setFilterDatasetId('');
              setFilterChainId('');
              setFilterIngestedBy('');
            }}
          >
            Clear
          </button>
        )}
        <div style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text-secondary)' }}>
          {loading ? 'Loading…' : `${samples.length} shown`}
        </div>
      </div>

      <div
        className="eval-inference-grid"
        style={{ opacity: loading ? 0.4 : 1, transition: 'opacity 0.15s' }}
      >
        {samples.map((inf) => (
          <GtSampleCard
            key={inf.gt_sample_id}
            sample={inf}
            onClick={() => navigate(`/eval/gt-samples/${inf.gt_sample_id}`)}
            showSampleId
            showIngestionInfo
            showStatusBadges
            datasetName={
              inf.dataset_id
                ? (datasets.find((d) => d.dataset_id === inf.dataset_id)?.dataset_name ??
                  inf.dataset_id)
                : null
            }
            actions={
              <>
                <button
                  className="eval-btn"
                  style={{ padding: '2px 7px', fontSize: 10 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open(`/gt/audit/${inf.gt_sample_id}`, '_blank');
                  }}
                >
                  Edit
                </button>
                {!inf.dataset_id && (
                  <button
                    className="eval-btn-danger"
                    style={{ padding: '2px 7px', fontSize: 10 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteTarget(inf);
                    }}
                  >
                    Delete
                  </button>
                )}
              </>
            }
          />
        ))}
        {!loading && samples.length === 0 && (
          <div style={{ color: 'var(--text-secondary)', fontSize: 13, gridColumn: '1/-1' }}>
            No samples found.
          </div>
        )}
      </div>
      <LoadMoreBar shown={samples.length} total={total} loading={loading} onLoadMore={loadMore} />

      {deleteTarget && (
        <DeleteConfirmDialog
          title="Delete GT Sample"
          description="This will permanently delete GT sample"
          itemId={deleteTarget.gt_sample_id}
          consequence="Its ground-truth annotations and correction history will be lost."
          onConfirm={async () => {
            try {
              await EvalApiService.deleteGtSample(token ?? '', deleteTarget.gt_sample_id);
              setDeleteTarget(null);
              reload();
            } catch (err) {
              setDeleteTarget(null);
              window.alert(err instanceof Error ? err.message : 'Error deleting GT sample');
            }
          }}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
};
